/**
 * DialogueSystem.ts - FF1-style dialogue box with typewriter effect
 * Classic blue gradient background with white text, just like the NES original
 */

import { Renderer } from './Renderer';
import { GameInput, GameKey } from './types/engine';
import { DialogueLine } from './data/StoryData';

interface DialogueState {
  active: boolean;
  currentLine: DialogueLine | null;
  displayedText: string;
  fullText: string;
  charIndex: number;
  typewriterSpeed: number;
  typewriterTimer: number;
  waitingForInput: boolean;
  blinkTimer: number;
  showContinueArrow: boolean;
  onComplete: (() => void) | null;
}

export class DialogueBox {
  private state: DialogueState;

  // Box dimensions (FF1 style - bottom of screen)
  private readonly BOX_X = 8;
  private readonly BOX_Y = 168;
  private readonly BOX_WIDTH = 240;
  private readonly BOX_HEIGHT = 64;
  private readonly PADDING = 8;
  private readonly LINE_HEIGHT = 16;

  // Typewriter settings
  private readonly CHARS_PER_FRAME = 2; // How fast text appears
  private readonly BLINK_SPEED = 400; // Continue arrow blink rate

  constructor() {
    this.state = {
      active: false,
      currentLine: null,
      displayedText: '',
      fullText: '',
      charIndex: 0,
      typewriterSpeed: this.CHARS_PER_FRAME,
      typewriterTimer: 0,
      waitingForInput: false,
      blinkTimer: 0,
      showContinueArrow: true,
      onComplete: null,
    };
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Show a dialogue line
   */
  show(line: DialogueLine, onComplete?: () => void): void {
    this.state.active = true;
    this.state.currentLine = line;
    this.state.fullText = line.text;
    this.state.displayedText = '';
    this.state.charIndex = 0;
    this.state.waitingForInput = false;
    this.state.onComplete = onComplete || null;
    this.state.blinkTimer = 0;
    this.state.showContinueArrow = true;
  }

  /**
   * Show raw text (simpler API)
   */
  showText(text: string, speaker?: string, onComplete?: () => void): void {
    this.show(
      {
        speaker: speaker || '',
        text: text,
      },
      onComplete
    );
  }

  /**
   * Hide dialogue box
   */
  hide(): void {
    this.state.active = false;
    this.state.currentLine = null;
    this.state.displayedText = '';
    this.state.waitingForInput = false;
  }

  /**
   * Check if dialogue is active
   */
  isActive(): boolean {
    return this.state.active;
  }

  /**
   * Check if waiting for player input
   */
  isWaitingForInput(): boolean {
    return this.state.waitingForInput;
  }

  /**
   * Skip typewriter effect (show full text immediately)
   */
  skipTypewriter(): void {
    if (!this.state.waitingForInput) {
      this.state.displayedText = this.state.fullText;
      this.state.charIndex = this.state.fullText.length;
      this.state.waitingForInput = true;
    }
  }

  // =========================================================================
  // Update & Input
  // =========================================================================

  update(deltaTime: number): void {
    if (!this.state.active) return;

    // Blink the continue arrow
    this.state.blinkTimer += deltaTime;
    if (this.state.blinkTimer >= this.BLINK_SPEED) {
      this.state.blinkTimer = 0;
      this.state.showContinueArrow = !this.state.showContinueArrow;
    }

    // Typewriter effect
    if (!this.state.waitingForInput) {
      this.state.typewriterTimer += deltaTime;

      // Add characters based on speed
      while (
        this.state.typewriterTimer >= 16 && // ~60fps
        this.state.charIndex < this.state.fullText.length
      ) {
        this.state.typewriterTimer -= 16;

        // Add multiple characters per frame for speed
        for (let i = 0; i < this.state.typewriterSpeed; i++) {
          if (this.state.charIndex < this.state.fullText.length) {
            this.state.displayedText += this.state.fullText[this.state.charIndex];
            this.state.charIndex++;
          }
        }
      }

      // Check if text is complete
      if (this.state.charIndex >= this.state.fullText.length) {
        this.state.waitingForInput = true;
      }
    }
  }

  handleInput(input: GameInput): boolean {
    if (!this.state.active) return false;

    // A button advances dialogue
    if (input.justPressed.has(GameKey.A)) {
      if (this.state.waitingForInput) {
        // Text is complete, move to next dialogue or close
        this.advance();
        return true;
      } else {
        // Skip typewriter effect
        this.skipTypewriter();
        return true;
      }
    }

    // B button can also skip typewriter
    if (input.justPressed.has(GameKey.B) && !this.state.waitingForInput) {
      this.skipTypewriter();
      return true;
    }

    return false;
  }

  /**
   * Advance to next dialogue or complete
   */
  private advance(): void {
    if (this.state.onComplete) {
      this.state.onComplete();
    }
    this.hide();
  }

  // =========================================================================
  // Rendering
  // =========================================================================

  render(renderer: Renderer): void {
    if (!this.state.active) return;

    // Draw the classic FF1 blue dialogue box with gradient
    this.drawDialogueBox(renderer);

    // Draw speaker name if present
    if (this.state.currentLine?.speaker && this.state.currentLine.speaker !== 'NARRATOR') {
      this.drawSpeakerName(renderer, this.state.currentLine.speaker);
    }

    // Draw text with word wrapping
    this.drawText(renderer);

    // Draw continue arrow if waiting for input
    if (this.state.waitingForInput && this.state.showContinueArrow) {
      this.drawContinueArrow(renderer);
    }
  }

  private drawDialogueBox(renderer: Renderer): void {
    const x = this.BOX_X;
    const y = this.BOX_Y;
    const w = this.BOX_WIDTH;
    const h = this.BOX_HEIGHT;

    // Draw gradient background (FF1 blue)
    renderer.drawGradient(
      x,
      y,
      w,
      h,
      '#0000AA', // Top - darker blue
      '#0000FF'  // Bottom - brighter blue
    );

    // Draw white border (double border FF1 style)
    renderer.drawRect(x - 2, y - 2, w + 4, h + 4, '#FFFFFF', false);
    renderer.drawRect(x - 1, y - 1, w + 2, h + 2, '#FFFFFF', false);

    // Inner shadow effect
    renderer.drawRect(x, y, w, 2, 'rgba(0, 0, 0, 0.3)', true);
  }

  private drawSpeakerName(renderer: Renderer, speaker: string): void {
    // Draw speaker name above the box with a small background
    const nameWidth = speaker.length * 8 + 16;
    const nameX = this.BOX_X + 8;
    const nameY = this.BOX_Y - 18;

    // Background for name
    renderer.drawRect(nameX - 4, nameY - 2, nameWidth, 16, '#000080', true);
    renderer.drawRect(nameX - 4, nameY - 2, nameWidth, 16, '#FFFFFF', false);

    // Draw name
    renderer.drawText(speaker.toUpperCase(), nameX, nameY, {
      font: 'ff1',
      fontSize: 8,
      color: '#FFFFFF',
      shadow: false,
    });
  }

  private drawText(renderer: Renderer): void {
    const lines = this.wrapText(this.state.displayedText, 28); // ~28 chars per line
    const textX = this.BOX_X + this.PADDING;
    const textY = this.BOX_Y + this.PADDING;

    lines.forEach((line, index) => {
      if (index < 3) { // Max 3 lines
        renderer.drawText(line, textX, textY + index * this.LINE_HEIGHT, {
          font: 'ff1',
          fontSize: 8,
          color: '#FFFFFF',
          shadow: false,
        });
      }
    });
  }

  private drawContinueArrow(renderer: Renderer): void {
    // Draw blinking down arrow at bottom-right of box (FF1 style)
    const arrowX = this.BOX_X + this.BOX_WIDTH - 16;
    const arrowY = this.BOX_Y + this.BOX_HEIGHT - 12;

    // Draw a small triangle pointing down
    renderer.drawText('▼', arrowX, arrowY, {
      font: 'ff1',
      fontSize: 8,
      color: '#FFFFFF',
      shadow: false,
    });
  }

  /**
   * Wrap text to fit within dialogue box
   */
  private wrapText(text: string, maxChars: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;

      if (testLine.length <= maxChars) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }
}

// ============================================================================
// Choice Dialogue Box - For branching conversations
// ============================================================================

interface ChoiceState {
  active: boolean;
  choices: string[];
  selectedIndex: number;
  onSelect: ((choice: number) => void) | null;
}

export class ChoiceDialogueBox {
  private state: ChoiceState;

  // Box dimensions
  private readonly BOX_X = 168;
  private readonly BOX_Y = 80;
  private readonly BOX_WIDTH = 80;
  private readonly PADDING = 8;
  private readonly LINE_HEIGHT = 16;
  private readonly CURSOR_OFFSET = 12;

  constructor() {
    this.state = {
      active: false,
      choices: [],
      selectedIndex: 0,
      onSelect: null,
    };
  }

  // =========================================================================
  // Public API
  // =========================================================================

  show(choices: string[], onSelect: (choice: number) => void): void {
    this.state.active = true;
    this.state.choices = choices;
    this.state.selectedIndex = 0;
    this.state.onSelect = onSelect;
  }

  hide(): void {
    this.state.active = false;
    this.state.choices = [];
  }

  isActive(): boolean {
    return this.state.active;
  }

  // =========================================================================
  // Update & Input
  // =========================================================================

  handleInput(input: GameInput): boolean {
    if (!this.state.active) return false;

    // Navigate choices
    if (input.justPressed.has(GameKey.UP)) {
      this.state.selectedIndex = Math.max(0, this.state.selectedIndex - 1);
      return true;
    }

    if (input.justPressed.has(GameKey.DOWN)) {
      this.state.selectedIndex = Math.min(
        this.state.choices.length - 1,
        this.state.selectedIndex + 1
      );
      return true;
    }

    // Confirm selection
    if (input.justPressed.has(GameKey.A)) {
      if (this.state.onSelect) {
        this.state.onSelect(this.state.selectedIndex);
      }
      this.hide();
      return true;
    }

    // Cancel (select last option or -1)
    if (input.justPressed.has(GameKey.B)) {
      if (this.state.onSelect) {
        this.state.onSelect(-1);
      }
      this.hide();
      return true;
    }

    return false;
  }

  // =========================================================================
  // Rendering
  // =========================================================================

  render(renderer: Renderer): void {
    if (!this.state.active) return;

    const boxHeight = this.PADDING * 2 + this.state.choices.length * this.LINE_HEIGHT;

    // Draw box
    renderer.drawGradient(
      this.BOX_X,
      this.BOX_Y,
      this.BOX_WIDTH,
      boxHeight,
      '#0000AA',
      '#0000FF'
    );

    // Border
    renderer.drawRect(
      this.BOX_X - 2,
      this.BOX_Y - 2,
      this.BOX_WIDTH + 4,
      boxHeight + 4,
      '#FFFFFF',
      false
    );

    // Draw choices
    this.state.choices.forEach((choice, index) => {
      const y = this.BOX_Y + this.PADDING + index * this.LINE_HEIGHT;

      // Highlight selected choice
      if (index === this.state.selectedIndex) {
        renderer.drawRect(
          this.BOX_X + 4,
          y - 2,
          this.BOX_WIDTH - 8,
          this.LINE_HEIGHT,
          'rgba(255, 255, 255, 0.2)',
          true
        );
      }

      // Draw cursor for selected item
      if (index === this.state.selectedIndex) {
        renderer.drawCursor(this.BOX_X + this.CURSOR_OFFSET - 8, y + 2);
      }

      // Draw text
      renderer.drawText(choice, this.BOX_X + this.CURSOR_OFFSET, y, {
        font: 'ff1',
        fontSize: 8,
        color: '#FFFFFF',
        shadow: false,
      });
    });
  }
}
